import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '../../../api/endpoints/settings';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

interface Props { settings?: StoreSettings; storeId: string; }

export function NotificationsTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [orderEmail, setOrderEmail] = useState(false);
  const [orderSms, setOrderSms] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [settlementsEmail, setSettlementsEmail] = useState('');

  useEffect(() => {
    if (settings?.notifications) {
      const n = settings.notifications as Record<string, unknown>;
      setOrderEmail((n.orderEmail ?? (typeof n.ordersEmail === 'string' ? !!n.ordersEmail : n.ordersEmail) ?? false) as boolean);
      setOrderSms((n.orderSms ?? false) as boolean);
      const recipients = (n.emailRecipients as string[] | undefined);
      const legacyEmail = typeof n.ordersEmail === 'string' ? n.ordersEmail : undefined;
      setEmailRecipient(recipients?.[0] ?? legacyEmail ?? '');
      setSettlementsEmail((n.settlementsEmail as string) ?? '');
    }
  }, [settings]);

  const mutation = useMutation({
    mutationFn: () => settingsApi.updateNotifications(storeId, {
      orderEmail,
      ordersEmail: emailRecipient || undefined,  // legacy expects the email address string
      orderSms,
      emailRecipients: emailRecipient ? [emailRecipient] : [],
      settlementsEmail: settlementsEmail || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold mb-4">{t('settings.notifications')}</h2>
        <Toggle label={t('settings.orderEmail')} description={t('settings.orderEmailDesc')} checked={orderEmail} onChange={setOrderEmail} />
        <Toggle label={t('settings.orderSms')} description={t('settings.orderSmsDesc')} checked={orderSms} onChange={setOrderSms} />
        <Input label={t('settings.emailRecipient')} type="email" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)} />
        <Input label={t('settings.settlementsEmail', 'Settlements Email')} type="email" value={settlementsEmail} onChange={(e) => setSettlementsEmail(e.target.value)} />
      </section>
      <div className="flex justify-end">
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>{t('common.save')}</Button>
      </div>
    </div>
  );
}
